import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../../utils/supabaseClient";
// Note: Authentication still uses direct supabase client, but profile queries use offlineSupabase
import { User } from "@supabase/supabase-js";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Separator } from "../ui/separator";
import { Label } from "../ui/label";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { generateRecaptchaToken, isRecaptchaConfigured, loadRecaptchaScript } from "../../utils/recaptcha";

const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get("invite");

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [recaptchaEnabled, setRecaptchaEnabled] = useState(false);

  // Check for message in location state when component mounts
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear the location state after reading
      window.history.replaceState({}, document.title);
    }

    // Check for valid session and auto-login
    const checkSessionAndAutoLogin = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Session check error:', error);
          return;
        }

        if (session && session.user) {
          // Valid session exists, redirect to dashboard
          console.log('✅ Valid session found, redirecting to dashboard');
          navigate('/dashboard');
        }
      } catch (error) {
        console.error('Error checking session:', error);
      }
    };

    checkSessionAndAutoLogin();

    // Initialize reCAPTCHA
    const enabled = isRecaptchaConfigured();
    setRecaptchaEnabled(enabled);

    // Load reCAPTCHA script if enabled
    if (enabled) {
      loadRecaptchaScript().catch((error) => {
        console.error('Failed to load reCAPTCHA script:', error);
      });
    }

    // Add listener for auth state changes to handle social login profile updates
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event: string, session: any) => {
      if (event === "SIGNED_IN" && session?.user) {
        // Check if user signed in with OAuth provider
        if (
          session.user.app_metadata.provider &&
          session.user.app_metadata.provider !== "email"
        ) {
          await updateUserProfile(session.user);
        }
      }
    });

    // Clean up subscription when component unmounts
    return () => {
      subscription.unsubscribe();
    };
  }, [location, navigate]);

  // Function to update user profile after social login
  const updateUserProfile = async (user: User) => {
    try {
      // Profile creation/update is now handled by the database trigger
      console.log(`User ${user.id} signed in with social login`);
      console.log('✅ Social login successful, UserContext will handle navigation');
    } catch (err) {
      console.error("Error during social login:", err);
      setError("Social login failed. Please try again.");
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: window.location.origin,
        },
      });

      if (error) throw error;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Google sign in failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      // Generate reCAPTCHA token if enabled
      let recaptchaToken = '';
      if (recaptchaEnabled) {
        try {
          recaptchaToken = await generateRecaptchaToken('login');
          console.log('✅ reCAPTCHA token generated for login');
        } catch (recaptchaError) {
          console.warn('reCAPTCHA token generation failed:', recaptchaError);
          // Continue with login even if reCAPTCHA fails (graceful degradation)
        }
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (error) throw error;
      if (data.user) {
        console.log('✅ Login successful for user:', data.user.id);

        // If invite code exists, accept the invite and join organization
        if (inviteCode) {
          try {
            const token = (await supabase.auth.getSession()).data.session?.access_token;
            const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:3001";

            // Accept the invite
            const acceptResponse = await fetch(`${apiUrl}/api/invites/${inviteCode}/accept`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ user_id: data.user.id }),
            });

            if (acceptResponse.ok) {
              const acceptResult = await acceptResponse.json();
              console.log('✅ Invite accepted successfully:', acceptResult);

              // Fetch organization name
              let organisationName = 'the organisation';
              if (acceptResult.tenant_id) {
                try {
                  const { data: orgData, error: orgError } = await supabase
                    .from('base_tenants')
                    .select('tenant_name')
                    .eq('tenant_id', acceptResult.tenant_id)
                    .single();

                  if (orgError) {
                    console.error('Error fetching organisation name:', orgError);
                  } else if (orgData?.tenant_name) {
                    organisationName = orgData.tenant_name;
                    console.log('Successfully fetched organisation name:', organisationName);
                  }
                } catch (error) {
                  console.error("Error fetching organisation name:", error);
                }
              }

              // Navigate to dashboard with organization joined state
              navigate('/dashboard', {
                state: {
                  joinedOrganisation: true,
                  organisationName: organisationName
                }
              });
              return;
            } else {
              console.error('Failed to accept invite:', await acceptResponse.text());
              // Continue with normal login flow if invite acceptance fails
            }
          } catch (inviteError) {
            console.error("Error accepting invite:", inviteError);
            // Continue with normal login flow if invite acceptance fails
          }
        }

        // Navigate to dashboard after successful login
        console.log('✅ Navigating to dashboard after successful login');
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-2">
          <img
            src="/sy-logo-dark.png"
            alt="Charlie - AI Concussion Co-Pilot"
            className="h-16 w-auto mx-auto object-contain dark:hidden"
          />
          <img
            src="/sy-logo-light.png"
            alt="Charlie - AI Concussion Co-Pilot"
            className="h-16 w-auto mx-auto object-contain hidden dark:block"
          />
          {/* <CardTitle className="text-2xl">Welcome back</CardTitle> */}
          <CardDescription>
            Please sign in to your account
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {successMessage && (
            <div className="p-4 rounded-md bg-green-100 border border-green-300 text-green-700 text-sm">
              {successMessage}
            </div>
          )}

          {error && (
            <div className="p-4 rounded-md bg-destructive/10 border border-destructive/30 text-destructive text-sm">
              {error}
            </div>
          )}


          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <Label htmlFor="email">Email address</Label>
              <Input
                id="email"
                type="email"
                placeholder="name@example.com"
                required
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  required
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-end">
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-primary hover:text-primary/80"
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <Separator />
            </div>
            <div className="relative flex justify-center">
              <span className="px-2 bg-card text-muted-foreground text-sm">
                Or continue with
              </span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleGoogleSignIn}
            disabled={loading}
          >
            <svg
              className="h-4 w-4 mr-2"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g transform="matrix(1, 0, 0, 1, 27.009001, -39.238998)">
                <path
                  fill="#4285F4"
                  d="M -3.264 51.509 C -3.264 50.719 -3.334 49.969 -3.454 49.239 L -14.754 49.239 L -14.754 53.749 L -8.284 53.749 C -8.574 55.229 -9.424 56.479 -10.684 57.329 L -10.684 60.329 L -6.824 60.329 C -4.564 58.239 -3.264 55.159 -3.264 51.509 Z"
                />
                <path
                  fill="#34A853"
                  d="M -14.754 63.239 C -11.514 63.239 -8.804 62.159 -6.824 60.329 L -10.684 57.329 C -11.764 58.049 -13.134 58.489 -14.754 58.489 C -17.884 58.489 -20.534 56.379 -21.484 53.529 L -25.464 53.529 L -25.464 56.619 C -23.494 60.539 -19.444 63.239 -14.754 63.239 Z"
                />
                <path
                  fill="#FBBC05"
                  d="M -21.484 53.529 C -21.734 52.809 -21.864 52.039 -21.864 51.239 C -21.864 50.439 -21.724 49.669 -21.484 48.949 L -21.484 45.859 L -25.464 45.859 C -26.284 47.479 -26.754 49.299 -26.754 51.239 C -26.754 53.179 -26.284 54.999 -25.464 56.619 L -21.484 53.529 Z"
                />
                <path
                  fill="#EA4335"
                  d="M -14.754 43.989 C -12.984 43.989 -11.404 44.599 -10.154 45.789 L -6.734 42.369 C -8.804 40.429 -11.514 39.239 -14.754 39.239 C -19.444 39.239 -23.494 41.939 -25.464 45.859 L -21.484 48.949 C -20.534 46.099 -17.884 43.989 -14.754 43.989 Z"
                />
              </g>
            </svg>
            Sign in with Google
          </Button>
        </CardContent>
        <CardFooter className="flex flex-col space-y-4">
          <div className="relative w-full">
            <div className="absolute inset-0 flex items-center">
              <Separator />
            </div>
            <div className="relative flex justify-center">
              <span className="px-2 bg-card text-muted-foreground text-sm">
                New to the platform?
              </span>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full border-primary text-primary"
            asChild
          >
            <Link to="/signup">Create an account</Link>
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default Login;
